/* ============================================================
 * N2T ship-week — cart-stage ZIP capture
 *
 * Companion to assets/ship-week.js (PDP pill). Renders into the
 * cart drawer + cart page via snippets/ship-week-cart.liquid. On
 * 5-digit ZIP entry, computes the customer's reachable ship week
 * and writes five cart attributes via POST /cart/update.js:
 *
 *   ship_week_zip          — what the customer typed (5 digits)
 *   ship_week_zip_source   — "user" (vs default warehouse fallback)
 *   ship_week_start        — Mon ISO of the ship-date week
 *   ship_week_arrives_by   — actual target arrival ISO (Thu by default)
 *   ship_week_zone         — transit_days as a string
 *   ship_week_computed_at  — ISO set-at timestamp. Promises older than 24h
 *                            (or already past) are recomputed or cleared
 *                            by the checkout guard below before checkout.
 *
 * The compute algorithm is intentionally a parallel implementation
 * of ship-week.js#computeArrival rather than a shared module — both
 * files load via <script> tags with no bundler, and the algorithm is
 * small + well-tested. Any rule change must update BOTH files.
 * ============================================================ */

(function () {
  'use strict';

  var SAFETY_WEEKS = 12;

  // ----- pure date helpers ---------------------------------------------------

  function isoDate(d) {
    var y = d.getFullYear();
    var m = String(d.getMonth() + 1).padStart(2, '0');
    var dd = String(d.getDate()).padStart(2, '0');
    return y + '-' + m + '-' + dd;
  }

  function isoTimestamp(d) {
    return d.toISOString();
  }

  function weekMondayOf(date) {
    var d = new Date(date);
    d.setHours(0, 0, 0, 0);
    var day = d.getDay();
    var offsetToMonday = day === 0 ? -6 : 1 - day;
    d.setDate(d.getDate() + offsetToMonday);
    return d;
  }

  function weekFridayOf(date) {
    var monday = weekMondayOf(date);
    var friday = new Date(monday);
    friday.setDate(monday.getDate() + 4);
    return friday;
  }

  // Returns the warehouse-local hour (Central time). Mirrors ship-week.js.
  function getWarehouseHour(date) {
    try {
      var fmt = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/Chicago',
        hour: 'numeric',
        hour12: false,
      });
      return parseInt(fmt.format(date), 10);
    } catch (e) {
      return date.getHours();
    }
  }

  // ----- compute (parallel implementation of ship-week.js#computeArrival) ----

  function computeArrival(targetArrivalDay, transitDays, today) {
    var win = (typeof window !== 'undefined') ? window : {};
    var cfg = win.N2T_SHIP_WAREHOUSE || {};
    var cutoffHour = (typeof cfg.daily_cutoff_hour === 'number' && cfg.daily_cutoff_hour < 24)
      ? cfg.daily_cutoff_hour
      : 24;
    var blackouts = new Set(cfg.blackout_dates || []);

    var ref = today || new Date();
    var refMidnight = new Date(ref);
    refMidnight.setHours(0, 0, 0, 0);

    var effectiveOrderDate = new Date(refMidnight);
    if (getWarehouseHour(ref) >= cutoffHour) {
      effectiveOrderDate.setDate(refMidnight.getDate() + 1);
    }

    var earliestArrival = new Date(effectiveOrderDate);
    earliestArrival.setDate(effectiveOrderDate.getDate() + transitDays);

    var candidate = new Date(refMidnight);
    var daysAhead = (targetArrivalDay - candidate.getDay() + 7) % 7;
    if (daysAhead === 0) daysAhead = 7;
    candidate.setDate(refMidnight.getDate() + daysAhead);

    for (var week = 0; week < SAFETY_WEEKS; week++) {
      var candidateIso = isoDate(candidate);
      var isBlacked = blackouts.has(candidateIso);
      var reachable = earliestArrival <= candidate;
      if (!isBlacked && reachable) {
        var ship = new Date(candidate);
        ship.setDate(candidate.getDate() - transitDays);
        return { ship_date: ship, arrival_date: new Date(candidate) };
      }
      candidate.setDate(candidate.getDate() + 7);
    }
    return null;
  }

  function parseMockDate(s) {
    if (typeof s !== 'string' || s.length === 0) return null;
    var d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  }

  // ----- cart-attribute payload builder (the unit under test) ---------------

  // Returns:
  //   { ok: true,  attributes: {...full-set...} }    — write happy path
  //   { ok: false, reason: 'empty', attributes: {...empty-strings...} }  — clear
  //   { ok: false, reason: 'invalid_format' }        — silent (don't POST)
  //   { ok: false, reason: 'unknown_zone' }          — silent (don't POST)
  //   { ok: false, reason: 'test_mode' }             — silent safeguard
  //
  // The 'empty' branch and (when opts.hasPrior is true) invalid_format /
  // unknown_zone branches return a clear-attributes payload — callers should
  // POST it to /cart/update.js to wipe stale ship_week_* values. Without
  // hasPrior, those branches return undefined attributes (silent reject) so
  // mid-typing state doesn't thrash the cart.
  function clearPayload() {
    return {
      ship_week_zip: '',
      ship_week_start: '',
      ship_week_arrives_by: '',
      ship_week_zone: '',
      ship_week_zip_source: '',
      ship_week_computed_at: '',
    };
  }

  function buildAttributePayload(rawZip, opts) {
    opts = opts || {};
    var now = opts.now || new Date();
    var cutoffDay = (typeof opts.cutoffDay === 'number') ? opts.cutoffDay : 4; // Thu
    var hasPrior = !!opts.hasPrior;

    if (opts.testMode) {
      return { ok: false, reason: 'test_mode' };
    }

    var zip = (typeof rawZip === 'string' ? rawZip : '').trim();
    // Take first 5 chars to handle ZIP+4 ("60201-1234") cleanly.
    if (zip.length > 5) zip = zip.slice(0, 5);

    if (zip.length === 0) {
      return { ok: false, reason: 'empty', attributes: clearPayload() };
    }

    if (!/^\d{5}$/.test(zip)) {
      return hasPrior
        ? { ok: false, reason: 'invalid_format', attributes: clearPayload() }
        : { ok: false, reason: 'invalid_format' };
    }

    var win = (typeof window !== 'undefined') ? window : {};
    var zone = win.N2T_SHIP_ZONES && win.N2T_SHIP_ZONES.lookup
      ? win.N2T_SHIP_ZONES.lookup(zip)
      : null;
    if (!zone) {
      return hasPrior
        ? { ok: false, reason: 'unknown_zone', attributes: clearPayload() }
        : { ok: false, reason: 'unknown_zone' };
    }

    var arrival = computeArrival(cutoffDay, zone.transit_days, now);
    if (!arrival) {
      return { ok: false, reason: 'unreachable' };
    }

    // Customer-facing labels use arrival-week Mon → Fri (NOT ship-date-week,
    // NOT actual Thu arrival). Two reasons:
    // 1. Cross-country: transit can push ship_date into the prior calendar
    //    week (Sun for transit=4 + Thu target). Anchoring to arrival week
    //    keeps "Ships week of X / Arrives by Y" coherent (same week).
    // 2. Friday end-of-week (vs Thursday actual) gives a 1-day buffer for
    //    carrier delays. Promise Fri, deliver Thu = customer-friendly.
    var monday = weekMondayOf(arrival.arrival_date);
    var friday = weekFridayOf(arrival.arrival_date);

    return {
      ok: true,
      attributes: {
        ship_week_zip: zip,
        ship_week_zip_source: 'user',
        ship_week_start: isoDate(monday),
        ship_week_arrives_by: isoDate(friday),
        ship_week_zone: String(zone.transit_days),
        ship_week_computed_at: isoTimestamp(now),
      },
    };
  }

  // ----- staleness guard (pure) ---------------------------------------------
  //
  // A ship-week promise is only valid for the session it was computed in.
  // 2026-10-12 audit (n2t-data/ship-week/ANOMALIES.md): 5 one-time orders
  // checked out with attributes computed 6–24 days earlier because the
  // recompute only ran when <ship-week-cart-zip> mounted. Customers who went
  // cart-link → checkout, or hit an express button, skipped it.
  //
  // assessShipWeek(attrs, {now, maxAgeMs}) →
  //   'none'  — no ship-week attributes on the cart
  //   'fresh' — computed_at within maxAgeMs AND arrives_by not in the past
  //             AND ship_week_start not before this week's Monday
  //   'stale' — anything else (incl. missing/garbled computed_at)
  var MAX_AGE_MS = 24 * 60 * 60 * 1000;
  var SHIP_WEEK_KEYS = [
    'ship_week_zip', 'ship_week_start', 'ship_week_arrives_by',
    'ship_week_zone', 'ship_week_zip_source', 'ship_week_computed_at',
  ];

  function hasShipWeek(attrs) {
    if (!attrs) return false;
    for (var i = 0; i < SHIP_WEEK_KEYS.length; i++) {
      var v = attrs[SHIP_WEEK_KEYS[i]];
      if (v !== undefined && v !== null && String(v) !== '') return true;
    }
    return false;
  }

  function assessShipWeek(attrs, opts) {
    opts = opts || {};
    var now = opts.now || new Date();
    var maxAge = typeof opts.maxAgeMs === 'number' ? opts.maxAgeMs : MAX_AGE_MS;
    if (!hasShipWeek(attrs)) return 'none';
    var computed = parseMockDate(attrs.ship_week_computed_at);
    if (!computed) return 'stale';
    var age = now.getTime() - computed.getTime();
    if (age > maxAge || age < -5 * 60 * 1000) return 'stale';
    var today = isoDate(now);
    if (!attrs.ship_week_arrives_by || String(attrs.ship_week_arrives_by) < today) return 'stale';
    if (!attrs.ship_week_start || String(attrs.ship_week_start) < isoDate(weekMondayOf(now))) return 'stale';
    return 'fresh';
  }

  // refreshPayload(attrs, opts) → attributes object to POST, or null when no
  // write is needed. Stale promises are recomputed from the stored ZIP; if
  // that ZIP no longer resolves (unknown zone, unreachable, garbage) the
  // ship_week_* keys are cleared — no promise beats a wrong promise.
  function refreshPayload(attrs, opts) {
    opts = opts || {};
    var state = assessShipWeek(attrs, opts);
    if (state !== 'stale') return null;
    var result = buildAttributePayload(attrs.ship_week_zip || '', {
      now: opts.now || new Date(),
      cutoffDay: opts.cutoffDay,
      hasPrior: true,
    });
    if (result.reason === 'test_mode') return null;
    if (result.ok) return result.attributes;
    return clearPayload();
  }

  // ----- web component (DOM glue, browser only) ------------------------------

  if (typeof window !== 'undefined' && typeof customElements !== 'undefined') {
    if (!customElements.get('ship-week-cart-zip')) {
      customElements.define(
        'ship-week-cart-zip',
        class ShipWeekCartZip extends HTMLElement {
          connectedCallback() {
            this.input = this.querySelector('input[name="attributes[ship_week_zip]"]');
            this.label = this.querySelector('.ship-week-cart__label');
            this.error = this.querySelector('.ship-week-cart__error');
            if (!this.input) return;

            this.cutoffDay = parseInt(this.dataset.cutoffDay || '4', 10);
            this.testMode = !!this.dataset.testMode;
            this.debounceMs = 400;

            // Prefetch zone data so the first commit() (debounced ~400ms
            // after typing) finds the cache hot. loadZones() is idempotent.
            if (window.N2T_SHIP_ZONES && typeof window.N2T_SHIP_ZONES.loadZones === 'function') {
              window.N2T_SHIP_ZONES.loadZones();
            }

            this.input.addEventListener('input', this.onInput.bind(this));

            // Codex P2: seed from PDP-stored ZIP if cart attributes missing.
            // ship-week.js (PDP pill) writes localStorage['n2t_ship_zip'] when
            // customer enters ZIP there. If they then add to cart and arrive
            // here without retyping, we'd lose the ship-week tag entirely.
            // Seed input + commit synchronously on connect when:
            //   1. input is empty (no Liquid-rendered value from cart.attributes)
            //   2. localStorage has a 5-digit ZIP saved by PDP
            // The commit runs through buildAttributePayload normally — same
            // validation, same write — just bypassing debounce on connect.
            this.maybeSeedFromPdp();
          }

          maybeSeedFromPdp() {
            // Two refresh paths run on every mount of this element (cart
            // drawer open, cart-page load, AJAX cart replacement):
            //
            //  A. Input already has a ZIP (Liquid rendered it from
            //     cart.attributes.ship_week_zip). Re-commit so the displayed
            //     "Ships week of X / Arrives by Y" is recomputed against
            //     today's date. Without this, the label is frozen at the
            //     timestamp the customer first typed the ZIP — a customer
            //     who left their cart for a few days sees a past Monday/
            //     Thursday and tickets in. This is the bug Ryan hit
            //     2026-05-08 with a stale "Ships week of Monday, May 4"
            //     label after entering the ZIP days earlier.
            //
            //  B. Input is empty (cart has no ZIP yet) but the customer
            //     entered one on the PDP — seed from localStorage and
            //     commit. Bypasses the typing-debounce because the seed
            //     should land in the cart synchronously on connect.
            if (this.input.value && this.input.value.trim().length > 0) {
              this.commit();
              return;
            }
            var stored = null;
            try { stored = localStorage.getItem('n2t_ship_zip'); } catch (e) { /* private mode */ }
            if (!stored || !/^\d{5}/.test(stored)) return;
            this.input.value = stored.slice(0, 5);
            this.commit();
          }

          // Detects whether cart already has a ship_week attribute. Reading the
          // Liquid-rendered label is enough — if there's text content, the
          // server already had ship_week_start when this snippet rendered.
          // Cheap, synchronous, no extra fetch.
          hasPriorAttributes() {
            if (this.label && this.label.textContent.trim().length > 0) return true;
            // Also catch the case where label was just cleared by us this tick:
            return !!this._lastWrittenZip;
          }

          onInput() {
            clearTimeout(this._timer);
            this._timer = setTimeout(this.commit.bind(this), this.debounceMs);
          }

          commit() {
            // Ensure the lazy ZIP→transit-days table is loaded BEFORE
            // running buildAttributePayload — `lookup()` is synchronous
            // and returns null until the fetch resolves, which would
            // mis-classify a valid ZIP as `unknown_zone` and (with
            // hasPrior) wipe stale cart attributes incorrectly.
            var self = this;
            var loader = (window.N2T_SHIP_ZONES && typeof window.N2T_SHIP_ZONES.loadZones === 'function')
              ? window.N2T_SHIP_ZONES.loadZones()
              : Promise.resolve();
            loader.then(function () { self._commitWithZones(); });
          }

          _commitWithZones() {
            var result = buildAttributePayload(this.input.value, {
              now: new Date(),
              cutoffDay: this.cutoffDay,
              testMode: this.testMode,
              hasPrior: this.hasPriorAttributes(),
            });

            if (result.reason === 'invalid_format' || result.reason === 'unknown_zone') {
              if (this.label) this.label.textContent = '';
              if (this.error) {
                this.error.textContent = result.reason === 'unknown_zone'
                  ? 'We don\'t ship to that ZIP yet. Email hello@nettotableseafoods.com.'
                  : '';
              }
              // Fall through if we got a clear-payload (hasPrior was true) —
              // we still need to POST it to wipe stale cart attributes.
              if (!result.attributes) return;
            } else if (this.error) {
              this.error.textContent = '';
            }

            if (!result.attributes) return; // test_mode / unreachable — silent

            var routes = (typeof window !== 'undefined' && window.routes) || {};
            var url = routes.cart_update_url || '/cart/update.js';
            var self = this;
            fetch(url, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ attributes: result.attributes }),
            })
              .then(function () {
                self._lastWrittenZip = result.ok ? result.attributes.ship_week_zip : '';
                if (result.ok) self.renderLabel(result);
              })
              .catch(function () { /* silent — user can re-enter */ });
          }

          renderLabel(result) {
            if (!result.ok || !this.label) return;
            var monday = new Date(result.attributes.ship_week_start + 'T00:00:00');
            var friday = new Date(result.attributes.ship_week_arrives_by + 'T00:00:00');
            var optsM = { weekday: 'long', month: 'long', day: 'numeric' };
            this.label.innerHTML =
              '<span class="ship-week-cart__line ship-week-cart__line--ship">Ships week of ' +
              escapeHtml(monday.toLocaleDateString('en-US', optsM)) +
              '</span><span class="ship-week-cart__line ship-week-cart__line--arrive">Arrives on or before ' +
              escapeHtml(friday.toLocaleDateString('en-US', optsM)) + '</span>' +
              '<span class="ship-week-cart__line ship-week-cart__line--zip">For delivery to ZIP ' +
              escapeHtml(result.attributes.ship_week_zip) + '. Shipping somewhere else? Update it here.</span>';
          }
        }
      );
    }
  }

  // ----- checkout guard (browser only) ---------------------------------------
  //
  // Runs on every storefront page (this file loads from layout/theme.liquid):
  //   • page load / bfcache restore / tab re-focus → refresh stale attrs.
  //     Covers abandoned-cart & cart-link emails that land on the storefront.
  //   • checkout-bound clicks/submits (name="checkout" buttons, <a href*=
  //     "/checkout">) → block, await refresh (≤ GUARD_TIMEOUT_MS), resume.
  //   • express buttons (Shop Pay / PayPal / Apple Pay render in iframes or
  //     Shopify web components we can't pause) → refresh on first pointer/
  //     focus contact. Combined with the page-load refresh this means the
  //     cart is already fresh before the button can be clicked.
  // Limits (cannot be fixed in theme code): links that go straight to
  // /checkouts/... (Shopify abandoned-checkout recovery, Skio renewals,
  // Buy-it-now on the PDP, which never carries cart attributes) — see
  // docs/SHIP-WEEK-STALE-GUARD.md.
  var GUARD_TIMEOUT_MS = 2500;

  function cartRoute(name, fallback) {
    var r = (typeof window !== 'undefined' && window.routes) || {};
    return r[name] || fallback;
  }

  var _inflight = null;
  function ensureFresh() {
    if (_inflight) return _inflight;
    var cutoffEl = document.querySelector('ship-week-cart-zip');
    var cutoffDay = cutoffEl ? parseInt(cutoffEl.dataset.cutoffDay || '4', 10) : 4;
    if (cutoffEl && cutoffEl.dataset.testMode) return Promise.resolve('test_mode');
    var zones = (window.N2T_SHIP_ZONES && typeof window.N2T_SHIP_ZONES.loadZones === 'function')
      ? Promise.resolve(window.N2T_SHIP_ZONES.loadZones()).catch(function () {})
      : Promise.resolve();
    var cartUrl = (cartRoute('cart_url', '/cart')).replace(/\/$/, '') + '.js';
    _inflight = Promise.all([
      fetch(cartUrl, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
        .then(function (r) { return r.json(); }),
      zones,
    ])
      .then(function (res) {
        var attrs = (res[0] && res[0].attributes) || {};
        var payload = refreshPayload(attrs, { now: new Date(), cutoffDay: cutoffDay });
        if (!payload) return 'unchanged';
        return fetch(cartRoute('cart_update_url', '/cart/update.js'), {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes: payload }),
        }).then(function () {
          syncWidgets(payload);
          return payload.ship_week_start ? 'recomputed' : 'cleared';
        });
      })
      .catch(function () { return 'error'; })
      .then(function (outcome) {
        var p = _inflight;
        setTimeout(function () { if (_inflight === p) _inflight = null; }, 0);
        try {
          document.dispatchEvent(new CustomEvent('n2t:ship-week-guard', { detail: { outcome: outcome } }));
        } catch (e) { /* old browsers */ }
        return outcome;
      });
    return _inflight;
  }

  function withTimeout(p) {
    return Promise.race([p, new Promise(function (res) { setTimeout(function () { res('timeout'); }, GUARD_TIMEOUT_MS); })]);
  }

  function syncWidgets(payload) {
    var els = document.querySelectorAll('ship-week-cart-zip');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (el.input) el.input.value = payload.ship_week_zip || '';
      if (payload.ship_week_start && el.renderLabel) el.renderLabel({ ok: true, attributes: payload });
      else if (el.label) el.label.textContent = '';
    }
  }

  function isCheckoutHref(href) {
    return typeof href === 'string' && /\/checkouts?(\/|\?|$)/.test(href.replace(/^https?:\/\/[^/]+/, ''));
  }

  function installGuard() {
    if (window.__n2tShipWeekGuard) return;
    window.__n2tShipWeekGuard = true;
    window.N2T_SHIP_WEEK_GUARD = { ensureFresh: ensureFresh, timeoutMs: GUARD_TIMEOUT_MS };

    ensureFresh();
    window.addEventListener('pageshow', function (e) { if (e.persisted) ensureFresh(); });
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') ensureFresh();
    });

    // Checkout submit: <button name="checkout"> inside /cart form.
    document.addEventListener('submit', function (e) {
      var form = e.target;
      var submitter = e.submitter;
      var isCheckout = (submitter && submitter.name === 'checkout') ||
        isCheckoutHref(form.getAttribute && form.getAttribute('action'));
      if (!isCheckout || form.__n2tGuardPassed) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      withTimeout(ensureFresh()).then(function () {
        form.__n2tGuardPassed = true;
        if (submitter && submitter.name === 'checkout' && !form.querySelector('input[type=hidden][name=checkout]')) {
          var h = document.createElement('input');
          h.type = 'hidden'; h.name = 'checkout'; h.value = submitter.value || '';
          form.appendChild(h);
        }
        HTMLFormElement.prototype.submit.call(form);
      });
    }, true);

    // Checkout links.
    document.addEventListener('click', function (e) {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey) return;
      var a = e.target && e.target.closest && e.target.closest('a[href]');
      if (!a || !isCheckoutHref(a.getAttribute('href')) || a.target === '_blank') return;
      e.preventDefault();
      withTimeout(ensureFresh()).then(function () { window.location.assign(a.href); });
    }, true);

    // Express buttons: can't pause them, so refresh on first contact.
    var EXPRESS = '.additional-checkout-buttons, .shopify-payment-button, shopify-accelerated-checkout, shopify-accelerated-checkout-cart, [data-shopify="dynamic-checkout-cart"]';
    ['pointerdown', 'focusin', 'mouseover', 'touchstart'].forEach(function (type) {
      document.addEventListener(type, function (e) {
        if (e.target && e.target.closest && e.target.closest(EXPRESS)) ensureFresh();
      }, { capture: true, passive: true });
    });
  }

  if (typeof window !== 'undefined' && typeof document !== 'undefined' && typeof fetch === 'function' &&
      !(typeof process !== 'undefined' && process.versions && process.versions.node && typeof module === 'object')) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', installGuard);
    else installGuard();
  }

  function escapeHtml(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  // ----- Node-only export (test-runner side, never browser) ------------------
  if (
    typeof module === 'object' &&
    module !== null &&
    typeof module.exports === 'object' &&
    typeof process !== 'undefined' &&
    process.versions != null &&
    process.versions.node != null
  ) {
    module.exports = {
      buildAttributePayload,
      parseMockDate,
      computeArrival,
      weekMondayOf,
      isoDate,
      assessShipWeek,
      refreshPayload,
      clearPayload,
      isCheckoutHref,
      MAX_AGE_MS,
    };
  }
})();
